/* eslint-disable no-nested-ternary */
/**
 * Valuation Result Component
 *
 * Handles the valuation result display and functionality.
 */
const popetyioApiUrl = process.env.API_URL;
/**
 * Initialize valuation result functionality
 * @param {Object} widget The widget instance
 */
export function initializeValuationResult(widget) {
  // Set up valuation result display
  setupValuationResultDisplay(widget);

  // Set up download report button
  // setupDownloadReportButton();
  setReportUrl();
}

/**
 * Set up valuation result display
 * @param {Object} widget The widget instance
 */
function setupValuationResultDisplay(widget) {
  // Update the valuation result when it becomes available
  document.addEventListener("widget-state-update", (event) => {
    if (event.detail && event.detail.valuationResult) {
      updateValuationDisplay(widget, event.detail.valuationResult);
    }
  });
}

/**
 * Update the valuation display with results
 * @param {Object} widget The widget instance
 * @param {Object} result The valuation result
 */
function updateValuationDisplay(widget, result) {
  const valuationResult = document.querySelector(".valuation-result");
  const confidenceBarFill = document.querySelector(".confidence-bar-fill");
  const confidenceText = document.querySelector(
    ".confidence-level span:last-child"
  );

  if (valuationResult && result) {
    // Format currency based on language
    const formatter = new Intl.NumberFormat(
      widget.config.language === "en"
        ? "en-US"
        : widget.config.language === "fr"
        ? "fr-CH"
        : widget.config.language === "de"
        ? "de-CH"
        : widget.config.language === "it"
        ? "it-CH"
        : "en-US",
      {
        style: "currency",
        currency: "CHF",
        maximumFractionDigits: 0,
      }
    );

    // Update the DOM elements
    valuationResult.textContent = `${formatter.format(
      result.price_interval.lower
    )} - ${formatter.format(result.price_interval.upper)}`;

    if (confidenceBarFill && result.confidence) {
      // Set confidence level (1-3)
      const confidencePercent = result.confidence * 33; // 1=33%, 2=66%, 3=100%
      confidenceBarFill.style.width = `${confidencePercent}%`;

      // Update confidence text
      if (confidenceText) {
        confidenceText.textContent = widget.getText(
          result.confidence === 1
            ? "low"
            : result.confidence === 2
            ? "medium"
            : "high"
        );
      }
    }
  }
}

/**
 * Set up download report button
 */
function setupDownloadReportButton() {
  const downloadButton = document.querySelector(
    ".result-actions .action-button:first-child"
  );

  if (downloadButton) {
    downloadButton.addEventListener("click", (event) => {
      // URL will be set when the result is available
      // If we don't have a URL yet, prevent the default action
      if (!downloadButton.getAttribute("data-report-url")) {
        event.preventDefault();
        alert(
          "Report is not yet available. Please try again in a few moments."
        );
      }
    });
  }
}

/**
 * Set report URL on the download button
 * @param {string} url The report URL
 */
export function setReportUrl(url) {
  const downloadButton = document.querySelector(
    ".result-actions .action-button:first-child"
  );

  if (downloadButton && url) {
    downloadButton.setAttribute("data-report-url", url);
    downloadButton.addEventListener("click", () => {
      window.open(url, "_blank");
    });
  }
}

// Function for making API call to get valuation
export async function submitValuation(formData, config) {
  // console.log("Submitting valuation data");

  // Prepare property data with defaults
  const propertyData = {
    property_category: formData.property.property_category,
    property_type: formData.property.property_type,
    living_area: formData.property.living_area,
    rooms_nb: formData.property.rooms_nb,
    bathrooms_nb: formData.property.bathrooms_nb,
    construction_year: formData.property.construction_year,
    land_area: formData.property.land_area,
    floor: parseInt(formData.property.floor, 10),
    egid: formData.property.egid,
    condition: formData.property.condition,
    coordinates: {
      longitude: formData.property.coordinates?.longitude,
      latitude: formData.property.coordinates?.latitude,
    },
    address: {
      street: formData.property.address?.street,
      city: formData.property.address?.city,
      zip: formData.property.address?.zip,
      country: formData.property.address?.country,
    },
    parking_indoor: formData.property.parking_indoor,
  };

  // Prepare contact data
  const contactData = {
    title: formData.contact.title,
    first_name: formData.contact.first_name,
    last_name: formData.contact.last_name,
    email: formData.contact.email,
    phone: formData.contact.phone,
    comment: formData.contact.comment,
    reason: formData.contact.reason,
    timeframe: formData.contact.timeframe,
    expected_price: formData.contact.expected_price,
  };

  // Prepare config data
  const configData = {
    primary_color: config.mainColor,
    secondary_color: config.secondaryColor,
    text_color: config.textColor,
    border_radius: config.borderRadius,
    font_family: config.fontFamily,
    lang: config.language,
    privacy_policy_url: config.privacyPolicyUrl,
    style: config.emailStyle || "sober",
    send_email_to_contact: config.sendEmailToContact || true,
  };

  return makeApiCall(`${popetyioApiUrl}/api/v2/widget/estimate/deal`, config.token, {
    property: propertyData,
    contact: contactData,
    config: configData,
  });
}

/**
 * Send SMS verification code
 */
export async function sendSmsCode(phone, language, token) {
  return makeApiCall(`${popetyioApiUrl}/api/v2/widget/estimate/smscode/send`, token, {
    phone_number: `+${phone}`,
    lang: language,
  });
}

/**
 * Verify SMS code
 */
export async function verifySmsCode(code, hash, token) {
  return makeApiCall(`${popetyioApiUrl}/api/v2/widget/estimate/smscode/verify`, token, {
    code,
    hash,
  });
}

// Generic API call function
function makeApiCall(url, token, data) {
  return fetch(url, {
    method: "POST",
    headers: {
      accept: "application/json",
      "x-api-key": token,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  })
    .then((response) => response.json())
    .catch((error) => {
      console.error("Error making API call:", error);
      throw error;
    });
}
